import { Helmet } from "react-helmet-async";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const SITE_URL = "https://abbotasia.co.uk"; // production canonical base

/**
 * Per-page <head> tags. Falls back to defaults from site_settings.
 *
 * Props:
 *   title          — page title (overrides default)
 *   description    — meta description
 *   og_title       — Open Graph title (defaults to title)
 *   og_description — Open Graph description (defaults to description)
 *   og_image       — Open Graph image absolute URL (falls back to site default hero)
 *   path           — current path, e.g. "/destinations/thailand" — used for canonical
 *   noindex        — when true, add <meta name="robots" content="noindex,nofollow">
 *   type           — Open Graph type ("website" | "article"). Default "website".
 */
export default function Seo({
  title, description, og_title, og_description, og_image, path, noindex, type,
}) {
  const { settings } = useSiteSettings() || {};
  const finalTitle = title || settings?.seo_default_title || "Abbot Asia — Private Journeys Across Asia";
  const finalDesc =
    description ||
    settings?.seo_default_description ||
    "Abbot Asia plans private, expertly guided journeys across Asia — Vietnam, Japan, Cambodia, Thailand and beyond. ABTA-protected, UK-based.";
  const ogT = og_title || finalTitle;
  const ogD = og_description || finalDesc;
  const ogImg =
    og_image ||
    settings?.seo_default_og_image ||
    `${SITE_URL}/og-default.jpg`;
  const canonical = path ? `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}` : SITE_URL;
  const ogType = type || "website";

  return (
    <Helmet>
      <title>{finalTitle}</title>
      {finalDesc && <meta name="description" content={finalDesc} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:site_name" content="Abbot Asia" />
      <meta property="og:title" content={ogT} />
      {ogD && <meta property="og:description" content={ogD} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      {ogImg && <meta property="og:image" content={ogImg} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogT} />
      {ogD && <meta name="twitter:description" content={ogD} />}
      {ogImg && <meta name="twitter:image" content={ogImg} />}
    </Helmet>
  );
}
